import { Component, EventEmitter, Output, Input, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef, inject, ViewChildren, QueryList } from '@angular/core';
import { KeyValue } from '@angular/common';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import * as _ from 'lodash';
import { Observable, of, Subscription } from 'rxjs';
import { filter, finalize, mergeMap, take, tap } from 'rxjs/operators';
import { UploadFile, UploadInput } from 'ngx-uploader-esb';
import moment from 'moment';

import { PlaylistItemDTO } from '../../core/models/view-models/playlist-item-models/playlist-item-dto.model';
import { PlaylistDTO } from '../../core/models/view-models/playlist-models/playlist-dto.model';
import { AppConstants } from '../../app.constants';
import { Launcher } from '../../core/models/entities/launcher.type';
import { LaunchersService } from '../../core/services/http-services/launchers.service';
import { ILauncherFeature } from '../../core/interfaces/launcher-feature.interface';
import { RssTickerConfigs } from '../../core/models/entities/rss-ticker.type';
import { FilesService } from '../../core/services/http-services/files.service';
import { PlaylistItemLinkModel } from '../../core/models/view-models/playlist-item-models/playlist-item-link.model';
import { CustomFileModel } from '../../core/models/entities/file.type';
import { RecentFileComponent } from '../tab-files/tab-recent-files/recent-file/recent-file.component';
import { PlaylistItemType } from '../../core/models/enums/playlist-item-type.enum';
import { FileComponent } from '../tab-files/tab-file/file/file.component';
import { MawChromeLauncherViewMode } from '../../core/enums/mcl-view-mode.enum';
import { PlaylistDialogMessagesEnum } from '../../core/enums/toastr-messages/playlist-dialog-messages.enum';
import { DeleteDialogService } from '../../core/services/dialog-services/delete-dialog.service';
import { PlaylistInnerItemLinkModel } from '../../core/models/view-models/playlist-item-models/playlist-inner-item-link.model';
import { PlaylistItemsService } from '../../core/services/http-services/playlistItems.service';

@Component({
  template: ``
})
export class ItemTabComponent implements OnDestroy, OnChanges {
  @Input() playlist: PlaylistDTO;
  @Input() launchers: Launcher[];
  @Input() playlistItem: PlaylistItemDTO;
  @Input() playlistItems: PlaylistItemDTO[] = [];
  @Input() oldPlaylistItems: PlaylistItemDTO[] = [];
  @Input() isToSetDefaultLauncher: boolean = false;
  @Input() hasRssForegroundItem: boolean;
  @Input() get addAnother(): boolean {
    return this._addAnother;
  }

  @Output() closeRequest = new EventEmitter<boolean>();
  @Output() addAnotherChange = new EventEmitter();
  set addAnother(value) {
    this._addAnother = value;
    this.addAnotherChange.emit(this._addAnother);
  }

  @ViewChildren(RecentFileComponent) recentFilesComponents: QueryList<RecentFileComponent>;
  @ViewChildren(FileComponent) filesComponents: QueryList<FileComponent>;

  _addAnother: boolean;
  playlistItemName: string;
  timeSpan: Date;
  subscriptions: Subscription = new Subscription();
  selectedLauncher: Launcher = null;
  selectedLaunchers: Launcher[] = [];
  isFitScreenAvailable: boolean = false;
  uploadedRssFile: UploadFile;
  playlistItemTypes = PlaylistItemType;
  recentFiles$: Observable<CustomFileModel[]>;
  uploadInput: EventEmitter<UploadInput> = new EventEmitter<UploadInput>();
  itemsToRemove: PlaylistItemDTO[] = [];

  protected filesService = inject(FilesService);
  protected launchersService = inject(LaunchersService);
  protected changeRef = inject(ChangeDetectorRef);
  protected dialog = inject(MatDialog);
  protected deleteDialogService = inject(DeleteDialogService);
  protected playlisItemsService = inject(PlaylistItemsService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('addAnother') && Object.keys(changes).length === 1) {
      return;
    }

    if (this.launchers.length) {
      if (this.playlistItem) {
        this.setSelectedLauncher(this.playlistItem);
      }
      else if (this.playlistItems?.length) {
        this.setPlaylistItemsLauncher();
      }
    }
  }

  addSwitchItem(index: number) {
    this.playlistItem.PlaylistItemLinks[index].InnerItemLinks.push(new PlaylistInnerItemLinkModel());
  }

  removeSwitchItem(itemIndex: number, innerItemIndex: number) {
    this.playlistItem.PlaylistItemLinks[itemIndex].InnerItemLinks.splice(innerItemIndex, 1);
  }

  setSelectedLauncher(item: PlaylistItemDTO): Launcher {
    if (this.isToSetDefaultLauncher || item.PlaylistItemType) {
      this.selectedLauncher = item.LauncherId
        ? this.launchers.find(x => x.Id === item.LauncherId)
        : this.launchers.find(x =>
          _.some(
            x.Paths,
            c =>
              item.LocalLauncher &&
              item.LocalLauncher.includes(c.Path)
          )
        );

      if (this.selectedLauncher) {
        this.getLauncherFeatures(
          this.selectedLauncher.LauncherTitle || this.selectedLauncher.ExecutableName, this.selectedLauncher.Id, item
        );
      }
      else {
        this.selectedLauncher = null;
      }

      return this.selectedLauncher;
    }

    return null;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  cancel() {
    this.addAnother = false;
    this.closeRequest.emit(true);
  }

  saveChanges(items: PlaylistItemDTO[]) {
    let rssConfigs: RssTickerConfigs;

    items.forEach((data: PlaylistItemDTO) => {
      const index = this.playlist.PlaylistItems.findIndex(
        x => x === data || (x.Id && x.Id === data.Id) || data.isToChangeExisted
      );

      if (data.RssConfigs?.Url || data.RssConfigs?.FileId) {
        rssConfigs = data.RssConfigs;
      } else {
        data.RssConfigs = null;
      }

      data.PdfScrollEnabled = data.SecondsToScrollPdfPage ?
        data.PdfScrollEnabled :
        false;

      data.PlaylistItemLinks.forEach(source => {
        source.ZoomLevel = source.ZoomLevel >= AppConstants.PLAYLIST_MIN_ZOOM_LEVEL ?
          source.ZoomLevel :
          AppConstants.PLAYLIST_MIN_ZOOM_LEVEL;
      });

      if (index >= 0) {
        // update
        this.playlist.PlaylistItems[index] = {...data};

        if (this.playlist.PlaylistItems[index].launcherFeatures && this.playlist.PlaylistItems[index].launcherFeatures.length) {
          const platlistItemId: number = this.playlist.PlaylistItems[index].Id;

          if (platlistItemId) {
            this.playlist.PlaylistItems[index].launcherFeatures.forEach((launcherFeature: ILauncherFeature) => {
              launcherFeature.FeatureValue = `${+launcherFeature.FeatureValue}`;
              launcherFeature.PlaylistItemId = platlistItemId;

              this.launchersService.setFeaturesToLauncher(launcherFeature).subscribe();
            });
          }
        }
      } else {
        // add
        data.PlaylistId = this.playlist.Id;

        if (data.PlaylistItemType === PlaylistItemType.Foreground) {
          const lastForegroundItemIndex = this.playlist.PlaylistItems.findIndex(item =>
            item.PlaylistItemType === PlaylistItemType.Foreground);
          this.playlist.PlaylistItems.splice(lastForegroundItemIndex + 1, 0, data);
        } else {
          this.playlist.PlaylistItems.push(data);
        }
      }
    });

    if (this.playlist.IsAndroid) {
      this.playlisItemsService.processFiles(items, this.playlist);
    }

    if (this.playlist.PlaylistItems.length > 1 && rssConfigs) {
      this.updateRssConfigsForMCLItems(rssConfigs);
    }

    if (this.itemsToRemove.length > 0) {
      this.playlist.PlaylistItems = this.playlist.PlaylistItems.filter(item =>
        !this.itemsToRemove.some(itemToRemove => itemToRemove.PlaylistItemLinks[0].FileId === item.PlaylistItemLinks[0].FileId)
      );
    }

    this.closeRequest.emit(false);
  }

  onLauncherChange(event: Launcher, isLocal: boolean = false, item: PlaylistItemDTO) {
    this.selectedLauncher = event;
    item.Launcher = event;
    item.RssConfigs = null;
    item.LayoutSplitViewType = +Object.keys(MawChromeLauncherViewMode)[0].split(AppConstants.MCL_MODE_ID_DELIMITER)[1];

    this.getLauncherFeatures(event.LauncherTitle || event.ExecutableName, event.Id, item);
    this.checkIfMawChromeSelected(event, item);

    if (item.PlaylistItemLinks?.length) {
      item.PlaylistItemLinks[0].ZoomLevel = AppConstants.DEFAULT_PLAYLIST_ZOOM_LEVEL;
      item.PlaylistItemLinks[0].RefreshInterval = null;
    }

    if (!this.selectedLauncher.IsFullScreen) {
      item.OpenInFullScreen = false;
    }
    item.LauncherId = event.Id !== null ? event.Id : null;
    if (!(this.playlist.MachinePhysicalAddress || isLocal)) {
      item.LocalLauncher = null;
    } else {
      item.LocalLauncher =
        event.Paths && event.Paths.length
          ? _.map(event.Paths, p => p.Path).join(';')
          : null;
    }

    this.onConfigureItemProperties(item, event);
  }

  valueAscOrder(a: KeyValue<number,string>, b: KeyValue<number,string>): number {
    return a.value.localeCompare(b.value);
  }

  public onChangeMCLMode(item: PlaylistItemDTO, selectedMode: string): void {
    const selectedValue: number = MawChromeLauncherViewMode[selectedMode];
    const isDecreased = item.PlaylistItemLinks.length > selectedValue;

    item.LayoutSplitViewType = +selectedMode.split(AppConstants.MCL_MODE_ID_DELIMITER)[1];
    item.PlaylistItemLinks.splice(selectedValue);
    this.calculateAndSetDuration(item, isDecreased);

    for (let i = item.PlaylistItemLinks.length; i < selectedValue; i++) {
      item.PlaylistItemLinks.push(new PlaylistItemLinkModel());
    }
  }

  public onChangeField(item: PlaylistItemDTO, index: number): void {
    const source = item.PlaylistItemLinks[index];

    source.IsFile = !source.IsFile;
    source.Link = '';
    source.FileId = null;
    source.File = null;
    source.RefreshInterval = null;
    source.duration = null;
    source.percentage = AppConstants.ZERO_DOWNLOADED_FILE;

    this.calculateAndSetDuration(item);
  }

  public onSourceChanged(e: Event, index: number, item: PlaylistItemDTO): void {
    const url = (e?.target as HTMLInputElement)?.value;

    if (item.PlaylistItemLinks[index]?.IsFile) {
      this.recentFiles$.pipe(
        take(1)
      ).subscribe(files => {
        const selectedFile = files.find(file => file.Id === item.PlaylistItemLinks[index].FileId);

        item.PlaylistItemLinks[index].percentage = AppConstants.ZERO_DOWNLOADED_FILE;
        item.PlaylistItemLinks[index].duration = selectedFile.Duration;

        item.PlaylistItemLinks[index].File = selectedFile;
        item.PlaylistItemLinks[index].FileId = selectedFile.Id;

        this.calculateAndSetDuration(item);

        if (this.filesService.getFileType(item, index).includes(AppConstants.MP4_FILE_TYPE)) {
          this.convertMP4ToWebm(item, index);
        }
      });
    }
    else if (AppConstants.YOUTUBE_VIDEO_REGULAR.test(url)) {
      this.getYoutubeVideoDuration(item, url, index, true);
    }
    else {
      item.PlaylistItemLinks[index].duration = null;
      this.calculateAndSetDuration(item);
    }
  }

  public calculateAndSetDuration(item: PlaylistItemDTO, isToChangeDuration: boolean = true): void {
    const duration = this.calculateMaxDuration(item);
    this.setDuration(item, duration, isToChangeDuration);
  }

  public setDuration(item: PlaylistItemDTO, lenght: number, isToChangeDuration: boolean) {
    const duration = moment('2015-01-01')
      .startOf('day')
      .seconds(lenght || moment.duration(item.Duration).asSeconds())
      .format('HH:mm:ss');

    if (isToChangeDuration) {
      item.Duration = duration;
      item.useExactDuration = true;
    }

    item.ExactVideoDuration = lenght ? duration : null;
    this.changeRef.markForCheck();
  }

  public getYoutubeVideoDuration(item: PlaylistItemDTO, value: string, index: number, isToSetNewDuration: boolean): void {
    this.launchersService.getVideoDuration(value).pipe(
      take(1)
    ).subscribe(duration => {
      item.PlaylistItemLinks[index].duration = moment('2015-01-01')
        .startOf('day')
        .seconds(duration)
        .format('HH:mm:ss');

      if (isToSetNewDuration) {
        this.calculateAndSetDuration(item);
      }
    });
  }

  public initializeRssConfigs(playlist: PlaylistDTO): RssTickerConfigs {
    const itemWithRssConfigs = playlist.PlaylistItems.find(item => item.RssConfigs);
    const { Id, ...configs } = { ...itemWithRssConfigs?.RssConfigs ?? {} };
    return Object.keys(configs).length ? configs as RssTickerConfigs : new RssTickerConfigs();
  }

  public initDurationsList(item: PlaylistItemDTO): void {
    item.PlaylistItemLinks.forEach((source, index) => {
      if (AppConstants.YOUTUBE_VIDEO_REGULAR.test(source.Link)) {
        this.getYoutubeVideoDuration(item, source.Link, index, false);
      }
      else {
        item.PlaylistItemLinks[index].duration = item.PlaylistItemLinks[index]?.File?.Duration;
      }
    });

    item.useExactDuration = item.ExactVideoDuration === item.Duration;
  }

  public onSetLauncherForAllItems(launcher: Launcher, playlistType: PlaylistItemType, fileIds?: string[]) {
    const fileComponents = playlistType === this.playlistItemTypes.RecentFile ?
      this.recentFilesComponents :
        this.filesComponents;

    fileComponents.forEach((fileComponent: RecentFileComponent | FileComponent) => {
      fileComponent.showSetLauncherOption = false;

      if (this.canSetLauncherForItem(fileIds, fileComponent, launcher, playlistType)) {
        fileComponent.selectedLauncher = launcher;
        fileComponent.playlistItem.Launcher = launcher;
        fileComponent.playlistItem.LauncherId = launcher.Id;
        this.onLauncherChange(
          launcher,
          false,
          fileComponent.playlistItem
        );
      }
    });
  }

  public onConfigureItemProperties(item: PlaylistItemDTO, launcher: Launcher) {
    if (launcher.ExecutableName?.toLowerCase().includes(AppConstants.CHROME_LAUNCHER_TITLE_PREFIX)) {
      item.IsMuted = true;
      item.IsContentStretched = true;
      item.RssConfigs = this.initializeRssConfigs(this.playlist);
    } else {
      item.IsMuted = false;
      item.IsContentStretched = false;
      item.RssConfigs = null;
      item.PlaylistItemLinks.splice(1);
    }
  }

  public onZoomInputChanged(event: Event, source: PlaylistItemLinkModel): void {
    const value: number = +(event.target as HTMLInputElement).value;
    (event.target as HTMLInputElement).value =
      (value < AppConstants.PLAYLIST_MIN_ZOOM_LEVEL ?
        AppConstants.PLAYLIST_MIN_ZOOM_LEVEL :
          value > AppConstants.PLAYLIST_MAX_ZOOM_LEVEL ?
          AppConstants.PLAYLIST_MAX_ZOOM_LEVEL :
            value).toString();

    source.ZoomLevel = +(event.target as HTMLInputElement).value;
  }

  public onStretchContentChanged(item: PlaylistItemDTO) {
    if (item.IsContentStretched) {
      item.PlaylistItemLinks.forEach(source => {
        source.ZoomLevel = AppConstants.DEFAULT_PLAYLIST_ZOOM_LEVEL;
      });
    }
  }

  public convertMP4ToWebm(itemToConvert: PlaylistItemDTO, index = 0) {
    itemToConvert.PlaylistItemLinks[index].convertingRequest =
      this.deleteDialogService.show(PlaylistDialogMessagesEnum.PlaylistMP4ToWebmConvertingMessage, false)
        .pipe(
          filter(result => !!result),
          tap(() => {
            itemToConvert.PlaylistItemLinks[index].isConverting = true;
            this.timeSpan = new Date();
          }),
          mergeMap(() => this.filesService.convertMP4ToWebm([itemToConvert.PlaylistItemLinks[index].FileId])),
          tap(file => {
            if (this.recentFiles$) {
              this.recentFiles$ = this.filesService.addFileToTheRecentFilesList(this.recentFiles$, file[0]);
            }
            if (!index) {
              itemToConvert.PlaylistFileName = file[0].FileName;
            }
            itemToConvert.PlaylistItemLinks[index].FileId = file[0].Id;
            itemToConvert.PlaylistItemLinks[index].File = file[0];
          }),
          finalize(() => {
            itemToConvert.PlaylistItemLinks[index].isConverting = false;
            itemToConvert.percentage = AppConstants.FULLY_DOWNLOADED_FILE;
            this.timeSpan = new Date();
          }),
          take(1)
        )
        .subscribe();
  }

  public convertPPTFile(itemToConvert: PlaylistItemDTO, isToConvertToWebm: boolean) {
    itemToConvert.PlaylistItemLinks[0].convertingRequest = (this.playlist.IsAndroid ?
      of(true) :
      this.deleteDialogService.show(PlaylistDialogMessagesEnum.PlaylistPPTConvertingMessage, false))
        .pipe(
          filter(result => !!result),
          tap(() => {
            itemToConvert.PlaylistItemLinks[0].isConverting = true;
            this.timeSpan = new Date();
          }),
          mergeMap(() => this.filesService.convertPresentationToImages(itemToConvert.PlaylistItemLinks[0].FileId, isToConvertToWebm)),
          tap(data => {
            const generatedFilesIds = data.map(file => file.Id);
            this.filesService.generateItem(data, this.playlistItems);

            const itemToRemoveIndex = this.playlistItems.findIndex(item =>
              item.PlaylistItemLinks[0].FileId === itemToConvert.PlaylistItemLinks[0].FileId);

            this.playlistItems.splice(itemToRemoveIndex, 1);
            this.oldPlaylistItems.splice(itemToRemoveIndex, 1);
            this.selectedLaunchers.splice(itemToRemoveIndex, 1);
            this.itemsToRemove.push(itemToConvert);

            if (itemToConvert.Launcher) {
              setTimeout(() => this.onSetLauncherForAllItems(itemToConvert.Launcher, this.playlistItemTypes.UploadFile, generatedFilesIds));
            }
          }),
          finalize(() => {
            itemToConvert.PlaylistItemLinks[0].isConverting = false;
            itemToConvert.percentage = AppConstants.FULLY_DOWNLOADED_FILE;
            this.timeSpan = new Date();
          }),
          take(1)
        )
        .subscribe();
  }

  public convertMP4ToLandscape(itemToConvert: PlaylistItemDTO) {
    const source = itemToConvert.PlaylistItemLinks[0];
    source.isConverting = true;
    this.timeSpan = new Date();

    source.convertingRequest =
      this.filesService.convertPortraitToLandscape([source.FileId]).pipe(
        tap(file => source.File = file[0]),
        finalize(() => {
          source.isConverting = false;
          itemToConvert.percentage = AppConstants.FULLY_DOWNLOADED_FILE;
          this.timeSpan = new Date();
        })
    ).subscribe();
  }

  public removeItem(index: number) {
    Object.assign(this.playlistItems[index], this.oldPlaylistItems[index]);

    this.playlistItems.splice(index, 1);
    this.oldPlaylistItems.splice(index, 1);
    this.selectedLaunchers.splice(index, 1);
    this.setPlaylistItemsLauncher();
  }

  public setLauncherBasedOnFileType(item: PlaylistItemDTO) {
    const fileName = item.PlaylistItemLinks[0].File.FileName;

    switch(fileName) {
      case fileName.match(AppConstants.PPT_FILE_TYPES_REGULAR)?.input:
        this.setItemLauncher(AppConstants.POWER_POINT_LAUNCHER_TITLE_PREFIX, item);
        break;

      case fileName.match(AppConstants.CONTENT_PREVIEW_VIDEO_FILE_TYPES_REGULAR)?.input:
        this.setItemLauncher(AppConstants.VLC_LAUNCHER_TITLE_PREFIX, item);
        break;

      case fileName.match(AppConstants.PDF_FILE_TYPES_REGULAR)?.input:
        this.setItemLauncher(AppConstants.CHROME_LAUNCHER_TITLE_PREFIX, item);
        break;
    }
  }

  private canSetLauncherForItem(
    fileIds: string[],
    fileComponent: RecentFileComponent | FileComponent,
    launcher: Launcher,
    playlistType: PlaylistItemType
  ): boolean {
    return (!fileIds || fileIds.includes(fileComponent.playlistItem.PlaylistItemLinks[0].FileId)) &&
      fileComponent.selectedLauncher?.Id !== launcher.Id &&
      (playlistType === this.playlistItemTypes.RecentFile ?
        (fileComponent as RecentFileComponent).IsChecked :
          true);
  }

  private checkIfMawChromeSelected(launcher: Launcher, item: PlaylistItemDTO): void {
    if (launcher.LauncherTitle.toLowerCase().includes(AppConstants.CHROME_LAUNCHER_TITLE_PREFIX)) {
      if (this.filesService.getFileType(item)?.includes(AppConstants.PPT_FILE_TYPE)) {
        this.convertPPTFile(item, true);
      }
      else if (!item.PlaylistItemLinks[0].isConverting && this.filesService.getFileType(item)?.includes(AppConstants.MP4_FILE_TYPE)) {
        this.convertMP4ToWebm(item);
      }
    }
    else {
      item.PlaylistItemLinks?.splice(1);
    }
  }

  private getLauncherFeatures(launcherExecutableName: string, launcherId: number, item: PlaylistItemDTO): void {
    this.isFitScreenAvailable = AppConstants.FIT_SCREEN_ENABLED_LAUNCHERS.includes(launcherExecutableName);

    if (this.isFitScreenAvailable) {
      this.subscriptions.add(
        this.launchersService.getLauncherFeatures(launcherId, item.Id || 0).subscribe((launcherFeatures: ILauncherFeature[]) => {
          if (launcherFeatures && launcherFeatures.length) {
            item.launcherFeatures = _.cloneDeep(launcherFeatures);
            this.changeRef.detectChanges();
          }
        })
      );
    } else {
      item.launcherFeatures = [];
    }
  }

  private updateRssConfigsForMCLItems(rssConfigs: RssTickerConfigs): void {
    this.playlist.PlaylistItems.forEach(item => {
      if (item.Launcher?.LauncherTitle.toLowerCase().includes(AppConstants.CHROME_LAUNCHER_TITLE_PREFIX)) {
        item.RssConfigs = {
          ...rssConfigs,
          Id: item.RssConfigs?.Id || 0
        };
      }
    });
  }

  private calculateMaxDuration(item: PlaylistItemDTO): number {
    return item.PlaylistItemLinks?.length
      ? Math.max(
        ...item.PlaylistItemLinks.map(source =>
          moment.duration(source.duration).asSeconds()))
      : null;
  }

  private setPlaylistItemsLauncher() {
    this.playlistItems.forEach((item, i) => this.selectedLaunchers[i] = this.setSelectedLauncher(item));
  }

  private setItemLauncher(launcherName: string, item: PlaylistItemDTO) {
    const launcher = this.launchers.find(launcher =>
      launcher.ExecutableName?.toLowerCase().includes(launcherName)
    );

    this.onLauncherChange(launcher, false, item);
    this.onConfigureItemProperties(item, launcher);
  }
}
